/**
 * Benefit levers — single source of truth for the field list, labels, units,
 * and display formatting. Shared by BenefitsTab (create/edit modal + summary),
 * TemplatesTab (chips + detail rows), and the ready-made templates.
 *
 * Cost semantics (mirror of computeCost() in apps/workers/src/handlers/comp-benefit.ts):
 *   percent     → % of annual base salary
 *   months      → × monthly salary (one-off / year)
 *   baht_month  → × 12 (recurring monthly amount)
 *   baht_year   → as-is (annual amount)
 *   baht_once   → as-is (one-off / year)
 * เมื่อเพิ่ม/แก้ field ที่นี่ ต้องอัปเดต computeCost ฝั่ง worker ให้ตรงด้วย.
 */
import type { BenefitLevers } from '../../../hooks';

export type { BenefitLevers };

export type LeverUnit = 'percent' | 'months' | 'baht_month' | 'baht_year' | 'baht_once';

export interface LeverField {
  key: keyof BenefitLevers;
  label: string; // ป้ายเต็ม (modal + detail rows)
  short: string; // ป้ายสั้น (chips)
  unit: LeverUnit;
  group: string; // หัวข้อจัดกลุ่มใน modal
}

export const LEVER_GROUPS = ['ฐานเงินเดือน/โบนัส', 'ประกัน/กองทุน', 'เบี้ย/ค่าใช้จ่ายรายเดือน', 'รายปี'] as const;

export const LEVER_FIELDS: LeverField[] = [
  { key: 'raise_pct', label: 'ปรับขึ้นเงินเดือน', short: 'ขึ้น', unit: 'percent', group: 'ฐานเงินเดือน/โบนัส' },
  { key: 'bonus_months', label: 'โบนัส', short: 'โบนัส', unit: 'months', group: 'ฐานเงินเดือน/โบนัส' },
  { key: 'reward_bonus', label: 'เงินรางวัล/โบนัสพิเศษ', short: 'เงินรางวัล', unit: 'baht_once', group: 'ฐานเงินเดือน/โบนัส' },
  { key: 'health_insurance', label: 'ประกันสุขภาพ', short: 'ประกันสุขภาพ', unit: 'baht_year', group: 'ประกัน/กองทุน' },
  { key: 'life_insurance', label: 'ประกันชีวิต/อุบัติเหตุ', short: 'ประกันชีวิต', unit: 'baht_year', group: 'ประกัน/กองทุน' },
  { key: 'provident_fund_pct', label: 'กองทุนสำรองเลี้ยงชีพ', short: 'กองทุนสำรองฯ', unit: 'percent', group: 'ประกัน/กองทุน' },
  { key: 'allowance', label: 'เบี้ยเลี้ยง', short: 'เบี้ยเลี้ยง', unit: 'baht_month', group: 'เบี้ย/ค่าใช้จ่ายรายเดือน' },
  { key: 'travel_allowance', label: 'ค่าเดินทาง', short: 'ค่าเดินทาง', unit: 'baht_month', group: 'เบี้ย/ค่าใช้จ่ายรายเดือน' },
  { key: 'phone_allowance', label: 'ค่าโทรศัพท์', short: 'ค่าโทร', unit: 'baht_month', group: 'เบี้ย/ค่าใช้จ่ายรายเดือน' },
  { key: 'position_allowance', label: 'ค่าตำแหน่ง', short: 'ค่าตำแหน่ง', unit: 'baht_month', group: 'เบี้ย/ค่าใช้จ่ายรายเดือน' },
  { key: 'meal_allowance', label: 'ค่าอาหาร', short: 'ค่าอาหาร', unit: 'baht_month', group: 'เบี้ย/ค่าใช้จ่ายรายเดือน' },
  { key: 'housing_allowance', label: 'ค่าที่พัก', short: 'ค่าที่พัก', unit: 'baht_month', group: 'เบี้ย/ค่าใช้จ่ายรายเดือน' },
  { key: 'uniform_allowance', label: 'ค่าชุด/ยูนิฟอร์ม', short: 'ค่าชุด', unit: 'baht_year', group: 'รายปี' },
];

const baht = (n: number) => `฿${n.toLocaleString('en-US')}`;

/** ป้ายหน่วยต่อท้าย label ในช่องกรอก เช่น " (%)", " (บาท/เดือน)" */
export function unitHint(unit: LeverUnit): string {
  switch (unit) {
    case 'percent': return ' (%)';
    case 'months': return ' (เดือน)';
    case 'baht_month': return ' (บาท/เดือน)';
    case 'baht_year': return ' (บาท/ปี)';
    case 'baht_once': return ' (บาท)';
  }
}

/** ค่าที่มนุษย์อ่าน เช่น "5%", "2 เดือน", "฿1,500/เดือน". */
export function fmtLever(unit: LeverUnit, v: number): string {
  switch (unit) {
    case 'percent': return `${v}%`;
    case 'months': return `${v} เดือน`;
    case 'baht_month': return `${baht(v)}/เดือน`;
    case 'baht_year': return `${baht(v)}/ปี`;
    case 'baht_once': return baht(v);
  }
}

/** chips สั้นๆ เช่น ["ขึ้น 5%", "โบนัส 2 เดือน", "ค่าเดินทาง ฿1,500/เดือน"] */
export function leverChips(levers: BenefitLevers): string[] {
  const out: string[] = [];
  for (const f of LEVER_FIELDS) {
    const v = levers[f.key];
    if (v) out.push(`${f.short} ${fmtLever(f.unit, Number(v))}`);
  }
  return out;
}

/** หนึ่งบรรทัดสรุป เช่น "ขึ้น 5% · โบนัส 2 เดือน · ประกันสุขภาพ ฿20,000/ปี" */
export function leverSummary(levers: BenefitLevers): string {
  const c = leverChips(levers);
  return c.length ? c.join(' · ') : '—';
}

/** label+value เต็มสำหรับ detail modal */
export function leverRows(levers: BenefitLevers): Array<{ label: string; value: string }> {
  const out: Array<{ label: string; value: string }> = [];
  for (const f of LEVER_FIELDS) {
    const v = levers[f.key];
    if (v) out.push({ label: f.label, value: fmtLever(f.unit, Number(v)) });
  }
  return out;
}
